import React, { useCallback, useMemo } from 'react'

import { Button, Skeleton } from 'antd'
import { HStack, Icon, T } from 'govwell-ui'
import { EntityVersionWithFieldsFragment } from 'types/graphql'

import { SelectValue } from 'src/components/Selects/EntityVersionSearch/types'
import { isSelectValueSearchEntityVersionFragment } from 'src/components/Selects/EntityVersionSearch/util'
import { ColumnData, FieldValueTable } from 'src/components/shared/FieldValueTable'
import useDisclosure from 'src/hooks/use-disclosure'

type Props = {
  entityVersion: SelectValue
  entityVersionWithFields?: EntityVersionWithFieldsFragment | null
  onRemove?: (entityVersion: SelectValue) => void
  isEntityVersionWithFieldsLoading?: boolean
  isCollapsible?: boolean
}

const EntityVersionSearchMapCard = ({
  entityVersion,
  entityVersionWithFields,
  onRemove,
  isEntityVersionWithFieldsLoading,
  isCollapsible,
}: Props) => {
  const { isOpen, toggle: toggleIsOpen } = useDisclosure()
  const handleRemove = useCallback(() => {
    onRemove?.(entityVersion)
  }, [entityVersion, onRemove])

  const label = isSelectValueSearchEntityVersionFragment(entityVersion)
    ? entityVersion.entity?.name
    : entityVersion

  const data: ColumnData[] = useMemo(() => {
    const fields =
      entityVersionWithFields?.fieldsForEntityVersionAndIntersectingEntityVersions ?? []
    return fields.map((f) => ({
      fieldName: f.entityFieldTemplate?.label || '',
      key: `${f.id}`,
      fieldValue: f.stringifiedValue || '',
    }))
  }, [entityVersionWithFields])

  const isEntityVersionFieldsVisible = !isCollapsible || isOpen

  return (
    <div className="w-full rounded-md border border-neutral-200 p-4">
      <HStack gap={2} justify="space-between">
        <HStack gap={2}>
          {isCollapsible && (
            <Button
              type="text"
              icon={isOpen ? <Icon.FaChevronDown /> : <Icon.FaChevronRight />}
              onClick={toggleIsOpen}
            />
          )}
          <T.Body className="font-semibold">{label}</T.Body>
        </HStack>
        {onRemove && <Button type="text" icon={<Icon.FaXmark />} onClick={handleRemove} />}
      </HStack>
      {isEntityVersionFieldsVisible && isEntityVersionWithFieldsLoading && <Skeleton />}
      {isEntityVersionFieldsVisible && !isEntityVersionWithFieldsLoading && !!data.length && (
        <div className="mt-3">
          <FieldValueTable data={data} />
        </div>
      )}
    </div>
  )
}

export default React.memo(EntityVersionSearchMapCard)
